import React, { useState, useEffect, useRef } from 'react';
import { useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CreditCard, History, Wallet, CheckCircle, AlertCircle, Loader2, Info } from 'lucide-react';
import { FaShoppingCart } from 'react-icons/fa';
import RAZORPAY_CONFIG from '../config/razorpay';
import { useUserDetails } from '../context/UserContext';

// Custom Header Component for Billing
const BillingHeader = ({ creditInfo }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { userDetails } = useUserDetails();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleUserProfileClick = () => {
    window.open(process.env.REACT_APP_USER_PROFILE, '_blank');
    setIsDropdownOpen(false);
  };

  const handleLogout = () => {
    signOut();
    setIsDropdownOpen(false);
  };

  const handleBuyCredits = () => {
    navigate('/pricing');
    setIsDropdownOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full">
      {/* Glass Header Container */}
      <div className="glass-effect border-b border-white/5 backdrop-blur-2xl bg-black/10">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 lg:px-6 py-1">
          <div className="flex items-center justify-between">
            
            {/* Left Section - Logo and Taco */}
            {!isSignedIn && (
              <div className="flex items-center gap-1 lg:gap-1.5">
                {/* Logo */}
                <img 
                  src="/logo.png" 
                  alt="Taco Logo" 
                  className="w-5 h-5 sm:w-6 sm:h-6"
                />
                
                {/* Taco Text */}
                <h1 className="text-sm sm:text-base lg:text-lg font-bold">
                  <span className="gradient-text">Taco</span>
                </h1>
              </div>
            )}

            {/* Right Section - Auth Buttons or Profile */}
            <div className="flex items-center gap-1 sm:gap-1.5 ml-auto">
              {isLoaded && (
                <>
                  {isSignedIn ? (
                    /* Profile Section with Dropdown - When Signed In */
                    <div className="relative" ref={dropdownRef}>
                      <div 
                        onClick={handleProfileClick}
                        className="flex items-center gap-1 sm:gap-1.5 cursor-pointer group transition-all duration-300 px-1 sm:px-1.5 md:px-2 py-0.5 rounded-full hover:bg-white/10"
                      >
                        {/* User Avatar */}
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full overflow-hidden border border-gray-300 group-hover:border-white transition-colors duration-300">
                          {user?.imageUrl ? (
                            <img 
                              src={user.imageUrl} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* User Name and Plan Info */}
                        <div className="flex flex-col items-start">
                          <span className="text-xs font-medium text-gray-200 group-hover:text-white transition-colors duration-300 leading-tight">
                            {user?.firstName} {user?.lastName}
                          </span>
                          {/* Plan info - more compact on mobile */}
                          <div className="flex items-center gap-0.5 sm:gap-1 mt-0 leading-tight">
                            <span className={`text-xs font-medium ${parseFloat(creditInfo?.credit_left_amt || userDetails?.credit_left_amt || '0') > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {parseFloat(creditInfo?.credit_left_amt || userDetails?.credit_left_amt || '0').toFixed(2)} credits
                            </span>
                          </div>
                        </div>
                        
                        {/* Dropdown Arrow */}
                        <svg className={`w-3 h-3 text-gray-400 group-hover:text-gray-200 transition-all duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className="absolute right-0 top-full mt-2 w-64 sm:w-72 bg-gray-900/95 border border-gray-700/50 rounded-xl shadow-2xl z-50 overflow-hidden backdrop-blur-xl">
                          {/* User Info Header */}
                          <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-700/50">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-gray-600">
                                {user?.imageUrl ? (
                                  <img 
                                    src={user.imageUrl} 
                                    alt="Profile" 
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-white font-medium text-sm">
                                  {user?.firstName} {user?.lastName}
                                </p>
                                <p className="text-gray-400 text-xs">
                                  {user?.primaryEmailAddress?.emailAddress}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="py-1 sm:py-2">
                            <button
                              onClick={() => {
                                navigate('/billing');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <FaShoppingCart className="text-gray-300" />
                              Buy More Credits
                            </button>
                            <button
                              onClick={() => {
                                window.open('mailto:manesh.pai@visualtaco.com?subject=Hi there!', '_blank');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Contact Us
                            </button>
                            <button
                              onClick={handleUserProfileClick}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              User Profile
                            </button>
                            <button
                              onClick={handleLogout}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Log out
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Log In and Sign Up Buttons - When Not Signed In */
                    <>
                      {/* Log In Button */}
                      <a 
                        href="/login" 
                        className="text-gray-300 hover:text-white transition-colors duration-300 px-1.5 sm:px-2 py-0.5 text-xs font-medium inline-block"
                      >
                        Log In
                      </a>
                      
                      {/* Sign Up Button */}
                      <a 
                        href="/sign-up" 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-2 sm:px-3 py-0.5 rounded-full text-xs font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block"
                      >
                        Sign Up
                      </a>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top glow effect */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
    </header>
  );
};

const Billing = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { userDetails } = useUserDetails();
  const [credits, setCredits] = useState(0);
  const [accountType, setAccountType] = useState('trial');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  // Credit info state for header
  const [creditInfo, setCreditInfo] = useState({ total_cost: 0, credit_left_amt: 0 });

  // Lazy load Razorpay script only when needed
  const loadRazorpay = async () => {
    // Check if already loaded
    if (window.Razorpay) {
      setRazorpayLoaded(true);
      return true;
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existingScript) {
      return new Promise((resolve) => {
        existingScript.onload = () => {
          setRazorpayLoaded(true);
          resolve(true);
        };
      });
    }

    return new Promise((resolve, reject) => {
      try {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => {
          setRazorpayLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          console.error('Failed to load Razorpay SDK');
          reject(new Error('Failed to load Razorpay SDK'));
        };
        document.body.appendChild(script);
      } catch (error) {
        console.error('Error loading Razorpay script:', error);
        reject(error);
      }
    });
  };

  // Fetch user credits and invoices
  useEffect(() => {
    if (user?.id && userDetails?.id) {
      fetchUserData();
    }
  }, [user?.id, userDetails?.id]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // Use userDetails from context instead of API call
      if (!userDetails?.id) {
        throw new Error('User details not available');
      }

      const userId = userDetails.id;

      // Fetch credits and invoices in parallel
      const [creditsResponse, invoicesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/credits/${userId}`, {
          headers: {
            'Authorization': `Bearer ${await getToken()}`
          }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/invoices/${userId}`, {
          headers: {
            'Authorization': `Bearer ${await getToken()}`
          }
        })
      ]);

      if (creditsResponse.ok) {
        const creditsData = await creditsResponse.json();
        if (creditsData.success) {
          setCredits(creditsData.credits);
          setAccountType(creditsData.account_type);
          // Update credit info for header
          setCreditInfo({
            total_cost: creditsData.total_cost || 0,
            credit_left_amt: creditsData.credits || 0
          });
        }
      }

      if (invoicesResponse.ok) {
        const invoicesData = await invoicesResponse.json();
        if (invoicesData.success) {
          setInvoices(invoicesData.invoices);
        }
      }

    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    const numAmount = parseFloat(amount);
    
    if (!amount || isNaN(numAmount)) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (numAmount < 1) {
      toast.error('Minimum amount is ₹1');
      return;
    }
    
    if (numAmount < 50) {
      toast.error('Minimum amount is ₹50');
      return;
    }

    // if (numAmount > 10000) {
    //   toast.error('Maximum amount is ₹10,000');
    //   return;
    // }

    try {
      setPaymentLoading(true);

      // Load Razorpay SDK if not already loaded
      if (!window.Razorpay) {
        try {
          await loadRazorpay();
        } catch (error) {
          toast.error('Failed to load payment gateway. Please refresh and try again.');
          return;
        }
      }

      // Use userDetails from context instead of API call
      if (!userDetails?.id) {
        throw new Error('User details not available');
      }

      const userId = userDetails.id;

      // Create order
      const orderResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          user_id: userId,
          amount: parseFloat(amount),
          userDetails: userDetails
        })
      });

      const orderData = await orderResponse.json();

      if (!orderData.success) {
        throw new Error(orderData.error);
      }

      // Configure Razorpay options
      const options = {
        key: RAZORPAY_CONFIG.keyId,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: RAZORPAY_CONFIG.companyName,
        description: 'Credit Purchase',
        order_id: orderData.order.id,
        prefill: {
          name: user.fullName || user.firstName + ' ' + user.lastName,
          email: user.primaryEmailAddress.emailAddress,
        },
        theme: {
          color: '#8B5CF6'
        },
        handler: async (response) => {
          try {
            // Verify payment
            const verifyResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await getToken()}`
              },
              body: JSON.stringify({
                payment_id: response.razorpay_payment_id,
                order_id: response.razorpay_order_id,
                signature: response.razorpay_signature,
                userDetails: userDetails
              })
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              toast.success(`Payment successful! ${verifyData.credits_added} credits added to your account.`);
              setAmount('');
              setShowPaymentForm(false);
              // Refresh user data
              await fetchUserData();
            } else {
              toast.error('Payment verification failed: ' + verifyData.error);
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentLoading(false);
          }
        }
      };

      // Open Razorpay checkout
      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Payment initiation error:', error);
      toast.error('Failed to initiate payment. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden">
        {/* Header */}
        <BillingHeader creditInfo={creditInfo} />
        
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-20 w-64 h-64 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        </div>
        
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mb-4 mx-auto"></div>
          <div className="text-white text-lg">Loading billing information...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black relative">
      {/* Header */}
      <BillingHeader creditInfo={creditInfo} />
      
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-20 w-64 h-64 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-4 pt-20 sm:pt-24 pb-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Billing & Credits</h1>
          <p className="text-gray-400">Manage your credits and view payment history</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Credits Overview */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-white">Pay as you go billing</h2>
                  <div className="flex items-center mt-1">
                    <span className="text-sm text-gray-300">Credit balance</span>
                    <div className="group relative ml-2">
                      <Info className="h-4 w-4 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-64 sm:w-auto">
                        <div className="text-center sm:text-left">
                          1 credit = 1 Rupee (₹). Your credit balance will be consumed as you use the API.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Wallet className="h-6 w-6 text-purple-500" />
              </div>
              
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-purple-500 mb-2">
                  {credits.toFixed(2)}
                </div>
                <div className="text-sm text-gray-400">
                  {accountType === 'paid' ? 'Paid Account' : 'Trial Account'}
                </div>
              </div>

              <button
                onClick={async () => {
                  if (!showPaymentForm) {
                    // Pre-load Razorpay when opening payment form
                    loadRazorpay().catch(err => {
                      console.error('Failed to pre-load Razorpay:', err);
                    });
                  }
                  setShowPaymentForm(!showPaymentForm);
                }}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
              >
                <CreditCard className="h-5 w-5 mr-2" />
                Purchase Credits
              </button>
            </div>

            {/* Payment Form */}
            {showPaymentForm && (
              <div className="bg-gray-800 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Purchase Credits</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    min="50"
                    max="10000"
                    step="1.00"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Minimum: ₹50
                  </p>
                </div>

                <div className="mb-4">
                  {(() => {
                    const numAmount = parseFloat(amount);
                    if (!amount || isNaN(numAmount)) {
                      return (
                        <p className="text-sm text-gray-400">
                          Enter an amount to see credit conversion
                        </p>
                      );
                    }
                    if (numAmount < 50) {
                      return (
                        <p className="text-sm text-red-400">
                          Minimum amount is ₹50
                        </p>
                      );
                    }
                    if (numAmount > 10000) {
                      return (
                        <p className="text-sm text-red-400">
                          Maximum amount is ₹10,000
                        </p>
                      );
                    }
                    return (
                      <p className="text-sm text-gray-400">
                        You will receive {amount} credits for ₹{amount}
                      </p>
                    );
                  })()}
                </div>

                <button
                  onClick={handlePayment}
                  disabled={paymentLoading || !amount || amount <= 0}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                >
                  {paymentLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Pay with Razorpay
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          
          {/* Payment History */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Payment History</h2>
                <History className="h-6 w-6 text-purple-500" />
              </div>

              {invoices.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No payment history found</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Your payment history will appear here after you make your first purchase
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300 hidden md:table-cell">Transaction ID</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Credits</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-gray-300">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr 
                          key={invoice.id}
                          className="border-b border-gray-700 hover:bg-gray-750 transition-colors"
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center">
                              {invoice.status === 'completed' ? (
                                <CheckCircle className="h-5 w-5 text-green-500" />
                              ) : (
                                <AlertCircle className="h-5 w-5 text-yellow-500" />
                              )}
                              <span className="ml-2 text-sm text-gray-300 capitalize hidden sm:inline">
                                {invoice.status}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-white whitespace-nowrap">
                              {new Date(invoice.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                            <div className="text-xs text-gray-400 md:hidden">
                              {new Date(invoice.created_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="py-4 px-4 hidden md:table-cell">
                            <div className="text-sm text-gray-400 font-mono">
                              {invoice.payment_id}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm text-white font-medium">
                              {invoice.amount} cr
                            </div>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <div className="text-sm font-semibold text-white whitespace-nowrap">
                              {formatAmount(invoice.amount)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Billing;
